import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { combineUtcDateAndTime, defaultCalendarFromUtc, defaultCalendarToUtc } from './growth-datetime.util';
import { GrowthOptimizationService, GrowthOptimizer } from './growth-optimization.service';

interface ObjectiveFieldRow {
  key: string;
  value: string;
}

@Component({
  standalone: true,
  selector: 'ain-growth-optimizer-console',
  imports: [
    FormsModule,
    RouterLink,
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    JalaliDateInputComponent,
    ButtonModule,
    CardModule,
    InputTextModule,
    SelectModule
  ],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.optimization.console.title')" [description]="t('growth.optimization.console.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.optimization.console.help.title'"
        [descriptionKey]="'growth.optimization.console.help.description'"
        [bulletKeys]="[
          'growth.optimization.console.help.bullets.safeDefault',
          'growth.optimization.console.help.bullets.workflow',
          'growth.optimization.console.help.bullets.noUnsafeExecution'
        ]" />

      <div class="toolbar">
        <button pButton type="button" severity="secondary" icon="pi pi-refresh" [label]="t('common.refresh')" (click)="loadOptimizers()"></button>
        <a routerLink="/growth/optimizer/optimization-runs" pButton severity="secondary" [label]="t('growth.optimization.actions.openRuns')"></a>
        <a routerLink="/growth/optimizer/guardrails" pButton severity="secondary" [label]="t('growth.optimization.actions.openGuardrails')"></a>
        <a routerLink="/growth/optimizer/automation" pButton severity="secondary" [label]="t('growth.optimization.actions.openAutomation')"></a>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="card">
          <div class="section-title"><h3>{{ t('growth.optimization.console.sections.optimizers') }}</h3></div>
          @if (optimizers().length === 0) {
            <ain-state type="empty" [title]="t('growth.optimization.console.emptyOptimizers')" />
          } @else {
            <div class="optimizer-grid">
              @for (optimizer of optimizers(); track optimizer.id) {
                <p-card [header]="optimizer.name">
                  <p class="meta">{{ optimizer.optimizerKey }} · {{ optimizer.optimizerType }}</p>
                  <p class="meta">{{ optimizer.defaultAutomationLevel }} · {{ optimizer.status }}</p>
                  <p class="meta">{{ optimizer.objective }}</p>
                  <p class="meta">
                    {{ optimizer.isKillSwitchEnabled ? t('growth.optimization.killSwitch.enabled') : t('growth.optimization.killSwitch.disabled') }}
                  </p>
                  <button pButton type="button" size="small" [label]="t('growth.optimization.actions.selectOptimizer')" (click)="selectOptimizer(optimizer)"></button>
                </p-card>
              }
            </div>
          }
        </section>

        <section class="card">
          <div class="section-title"><h3>{{ t('growth.optimization.console.sections.runOptimizer') }}</h3></div>
          <div class="grid">
            <ain-form-field [label]="t('growth.optimization.fields.optimizer')">
              <p-select [options]="optimizerOptions()" [(ngModel)]="runForm.optimizerId" optionLabel="label" optionValue="value" appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.optimization.fields.scopeType')">
              <p-select [options]="scopeTypeOptions" [(ngModel)]="runForm.scopeType" optionLabel="label" optionValue="value" appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.optimization.fields.scopeRefId')">
              <input pInputText [(ngModel)]="runForm.scopeRefId" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.optimization.fields.periodStart')">
              <ain-date-input [(ngModel)]="runForm.periodStartUtc" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.optimization.fields.periodEnd')">
              <ain-date-input [(ngModel)]="runForm.periodEndUtc" />
            </ain-form-field>
          </div>

          <div class="rows">
            <h4>{{ t('growth.optimization.fields.objectiveStructured') }}</h4>
            @for (row of objectiveRows; track $index) {
              <div class="row">
                <input pInputText [(ngModel)]="row.key" [placeholder]="t('growth.optimization.fields.objectiveKey')" />
                <input pInputText [(ngModel)]="row.value" [placeholder]="t('growth.optimization.fields.objectiveValue')" />
                <button pButton type="button" severity="danger" icon="pi pi-times" [text]="true" (click)="removeObjectiveRow($index)"></button>
              </div>
            }
            <button pButton type="button" severity="secondary" icon="pi pi-plus" [label]="t('common.add')" (click)="addObjectiveRow()"></button>
          </div>

          <div class="actions">
            <button pButton type="button" [label]="t('growth.optimization.actions.run')" [loading]="running()" (click)="runOptimizer()"></button>
          </div>

          @if (lastRunId()) {
            <p class="meta">
              {{ t('growth.optimization.console.lastRunCreated') }}
              <a [routerLink]="['/growth/optimizer/runs', lastRunId()]">{{ lastRunId() }}</a>
            </p>
          }
        </section>

        <section class="card warning">
          <div class="section-title">
            <h3>{{ t('growth.optimization.killSwitch.title') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.optimization.killSwitch.help.title'"
              [descriptionKey]="'growth.optimization.killSwitch.help.description'"
              [bulletKeys]="['growth.optimization.killSwitch.help.bullets.blockRun', 'growth.optimization.killSwitch.help.bullets.idempotent']" />
          </div>
          <div class="grid">
            <ain-form-field [label]="t('growth.optimization.fields.optimizer')">
              <p-select [options]="optimizerOptions()" [(ngModel)]="killSwitchOptimizerId" optionLabel="label" optionValue="value" appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.optimization.killSwitch.fields.reason')">
              <input pInputText [(ngModel)]="killSwitchReason" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.optimization.killSwitch.fields.confirmText')">
              <input pInputText [(ngModel)]="killSwitchConfirmText" />
            </ain-form-field>
          </div>
          <div class="actions">
            <button pButton type="button" severity="danger" [label]="t('growth.optimization.killSwitch.actions.enable')" (click)="enableKillSwitch()"></button>
          </div>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; display: grid; gap: 10px; margin-bottom: 12px; background: #fff; }
    .section-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .optimizer-grid { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); }
    .rows { display: grid; gap: 8px; }
    .row { display: grid; grid-template-columns: 1fr 1fr auto; gap: 8px; align-items: center; }
    .actions { display: flex; gap: 8px; }
    .warning { border-color: #f59e0b; background: #fff7ed; }
    .meta { color: #6b7280; }
  `]
})
export class GrowthOptimizerConsoleComponent {
  private readonly service = inject(GrowthOptimizationService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(false);
  readonly running = signal(false);
  readonly optimizers = signal<GrowthOptimizer[]>([]);
  readonly lastRunId = signal<string | null>(null);

  runForm = {
    optimizerId: '',
    scopeType: 'segment',
    scopeRefId: '',
    periodStartUtc: defaultCalendarFromUtc(),
    periodEndUtc: defaultCalendarToUtc()
  };

  readonly objectiveRows: ObjectiveFieldRow[] = [{ key: 'goal', value: 'increase-lead-conversion' }];
  killSwitchOptimizerId = '';
  killSwitchReason = '';
  killSwitchConfirmText = '';

  readonly scopeTypeOptions = [
    { label: 'Segment', value: 'segment' },
    { label: 'Content', value: 'content' },
    { label: 'Campaign', value: 'campaign' },
    { label: 'SeoProfile', value: 'seo-profile' }
  ];

  constructor() {
    this.loadOptimizers();
  }

  t(key: string): string { return this.i18n.translate(key); }

  optimizerOptions() {
    return this.optimizers().map(item => ({ label: `${item.name} (${item.optimizerType})`, value: item.id }));
  }

  loadOptimizers(): void {
    this.loading.set(true);
    this.service.listOptimizers().subscribe({
      next: items => {
        this.optimizers.set(items);
        if (!this.runForm.optimizerId && items.length > 0) this.runForm.optimizerId = items[0].id;
        if (!this.killSwitchOptimizerId && items.length > 0) this.killSwitchOptimizerId = items[0].id;
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  selectOptimizer(optimizer: GrowthOptimizer): void {
    this.runForm.optimizerId = optimizer.id;
  }

  addObjectiveRow(): void {
    this.objectiveRows.push({ key: '', value: '' });
  }

  removeObjectiveRow(index: number): void {
    this.objectiveRows.splice(index, 1);
    if (this.objectiveRows.length === 0) this.objectiveRows.push({ key: '', value: '' });
  }

  runOptimizer(): void {
    if (!this.runForm.optimizerId || !this.runForm.scopeRefId.trim()) return;
    this.running.set(true);
    const objectiveJson = JSON.stringify(this.objectiveRows.filter(item => item.key.trim()).map(item => ({ key: item.key.trim(), value: item.value.trim() })));
    this.service.runOptimizer(this.runForm.optimizerId, {
      scopeType: this.runForm.scopeType,
      scopeRefId: this.runForm.scopeRefId.trim(),
      periodStartUtc: combineUtcDateAndTime(this.runForm.periodStartUtc, '00:00'),
      periodEndUtc: combineUtcDateAndTime(this.runForm.periodEndUtc, '23:59'),
      objectiveJson,
      idempotencyKey: `optimizer-run-${this.runForm.optimizerId}-${Date.now()}`,
      correlationId: `optimizer-console-${this.runForm.optimizerId}`
    }).subscribe({
      next: response => {
        this.lastRunId.set(response.run.id);
        this.running.set(false);
      },
      error: () => this.running.set(false)
    });
  }

  enableKillSwitch(): void {
    if (!this.killSwitchOptimizerId || this.killSwitchConfirmText.trim().toUpperCase() !== 'ENABLE') return;
    this.service.enableKillSwitch(this.killSwitchOptimizerId, this.killSwitchReason.trim()).subscribe({
      next: () => this.loadOptimizers()
    });
  }
}
